import { Injectable, signal } from '@angular/core';

export const FONT_SCALE_LEVELS = [1.0, 1.15, 1.3, 1.5];
export const DEFAULT_FONT_SCALE = 1.0;

@Injectable({
  providedIn: 'root'
})
export class FontSizeService {
  private readonly FONT_SCALE_KEY = 'app-font-scale';
  private readonly _fontScale = signal<number>(DEFAULT_FONT_SCALE);

  readonly fontScale = this._fontScale.asReadonly();

  constructor() {
    this.initializeFontScale();
  }

  private initializeFontScale(): void {
    const savedScale = Number(localStorage.getItem(this.FONT_SCALE_KEY));
    if (Number.isFinite(savedScale) && FONT_SCALE_LEVELS.includes(savedScale)) {
      this._fontScale.set(savedScale);
    }
    this.applyFontScale();
  }

  increase(): void {
    const current = this._fontScale();
    const index = FONT_SCALE_LEVELS.indexOf(current);
    const next = FONT_SCALE_LEVELS[Math.min(index + 1, FONT_SCALE_LEVELS.length - 1)];
    this.setFontScale(next);
  }

  decrease(): void {
    const current = this._fontScale();
    const index = FONT_SCALE_LEVELS.indexOf(current);
    const next = FONT_SCALE_LEVELS[Math.max(index - 1, 0)];
    this.setFontScale(next);
  }

  reset(): void {
    this.setFontScale(DEFAULT_FONT_SCALE);
  }

  canIncrease(): boolean {
    return this._fontScale() < FONT_SCALE_LEVELS[FONT_SCALE_LEVELS.length - 1];
  }

  canDecrease(): boolean {
    return this._fontScale() > FONT_SCALE_LEVELS[0];
  }

  private setFontScale(scale: number): void {
    this._fontScale.set(scale);
    localStorage.setItem(this.FONT_SCALE_KEY, String(scale));
    this.applyFontScale();
  }

  private applyFontScale(): void {
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--app-font-scale', String(this._fontScale()));
    }
  }
}